import { useEffect, useRef, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { useReducedMotion } from "framer-motion";
import { greenText } from "../steps/stepStyles";

/**
 * The hook headline's rotating tail: types a word out, holds it, deletes it,
 * moves to the next. Reduced motion renders the LAST word (the general claim)
 * statically, so the headline still reads as a complete sentence.
 *
 * Rendered inline inside a Typography, so it inherits the headline's size and
 * weight and only colours itself.
 */

interface TypewriterWordsProps {
  words: string[];
}

const TYPE_MS = 75;
const DELETE_MS = 35;
const HOLD_MS = 1500;
const BETWEEN_MS = 260;

export const TypewriterWords = ({ words }: TypewriterWordsProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [chars, setChars] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const word = words[index] ?? "";

  useEffect(() => {
    if (reduced) return;
    let delay = TYPE_MS;
    let step = () => setChars((count) => count + 1);

    if (deleting) {
      delay = DELETE_MS;
      step = () => setChars((count) => count - 1);
    }
    if (!deleting && chars >= word.length) {
      delay = HOLD_MS;
      step = () => setDeleting(true);
    }
    if (deleting && chars <= 0) {
      delay = BETWEEN_MS;
      step = () => {
        setDeleting(false);
        setIndex((current) => (current + 1) % words.length);
      };
    }

    timer.current = setTimeout(step, delay);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [chars, deleting, word, words.length, reduced]);

  // The widest word reserves the line's width up front, so the headline never
  // reflows (and never bumps the demo above it) mid-animation.
  const widest = words.reduce((a, b) => (b.length > a.length ? b : a), "");
  const holding = reduced || (!deleting && chars >= word.length);

  return (
    <Box component="span" sx={{ position: "relative", whiteSpace: "nowrap" }}>
      <Box component="span" sx={{ visibility: "hidden" }} aria-hidden>
        {widest}
      </Box>
      <Box
        component="span"
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          // Centre the short words inside the width the longest one reserved,
          // otherwise they hang off to the left of the headline above.
          textAlign: "center",
          color: greenText(isDark),
        }}
      >
        {reduced ? words[words.length - 1] : word.slice(0, chars)}
        <Box
          component="span"
          sx={{
            display: "inline-block",
            // Slim and slightly short of cap height: at the headline's 30px
            // bold a full-height bar reads as a second glyph rather than a
            // caret.
            width: "2px",
            height: "0.72em",
            ml: "4px",
            borderRadius: "1px",
            opacity: 0.75,
            verticalAlign: "baseline",
            backgroundColor: greenText(isDark),
            "@keyframes typeCaretBlink": {
              "0%, 49%": { opacity: 1 },
              "50%, 100%": { opacity: 0 },
            },
            // Solid while it types or deletes, blinking only while it holds a
            // finished word. A blinking caret over an emptied tail leaves the
            // headline looking like a blank second line half the time.
            animation: holding ? "typeCaretBlink 1s step-end infinite" : "none",
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      </Box>
    </Box>
  );
};
